import {SGeneralForms, SLocalTreeSelected} from "shj-components";
import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import {Button, Input, message, Select, TreeSelect} from "antd";
import {httpOrgList} from "@/services/homeService.ts";
import {generalStandardSave, generalStandardUpdateById, getProductManagementPage} from "@/services/product.ts";
import {debounce, isEmpty} from "lodash-es";
import {useSetState} from "ahooks";
import {replaceRule, trimStr} from "@/utils/data.ts";
import DebounceSearchSelect from "@/pages/baseProduct/components/DebounceSearchSelect";
import RulesModal from "@/pages/baseProduct/components/RuleModal/RulesModal.tsx";

type IProps = {
  record: Record<string, any>
  chooseTypeKey?: string
  genItemId?: string
  fn: (data: Record<string, any>) => void
  setLoading: (flag: boolean) => void
  readOnly?: boolean
}

export const AddOrEditStandard = forwardRef((props: IProps, ref) => {
  const formRef = useRef();

  const [state, setState] = useSetState<any>({
    initValues: {},
    companyList: [],
    productList: [],
    budgetRule: '',
    searchValue: '',
  })

  const [ruleModal, setRuleModal] = useSetState({
    open: false,
    mode: 'modal',
    value: ''
  })

  const onFormRef = () => {
    const {onFormRef} = formRef.current || {};
    return onFormRef?.()
  }

  useEffect(() => {
    getOptions()
    if (!isEmpty(props.record)) {
      const {orgIdList: shops, budgetRule} = props.record.standardJson || {}
      const obj = {
        ...props.record,
        // editFlag:  props.record.editFlag,
        shops: shops?.map(item => item.id) || [],
        budgetRule: budgetRule,
      }
      setRuleModal({value: budgetRule})
      onFormRef()?.setFieldsValue(obj)
      setState({initValues: {...obj, shops}})
    } else {
      onFormRef()?.setFieldsValue({editFlag: props.chooseTypeKey !== 'GEN_STANDARD'})
    }
  }, [props.record]);

  useEffect(() => {
    getProduct(state.searchValue)
  }, [state.searchValue]);

  const getProduct = debounce(async (nameKey?) => {
    const data: any = await getProductManagementPage({pageId: 1, pageSize: 10, data: {enabled: true, nameKey}})
    setState({productList: data.list || []})
  }, 500)

  const getOptions = async () => {
    const companyList: any = await httpOrgList()
    setState({companyList})
  }

  const getShopArr = (codeArr) => {
    return [{id: '0', name: '通用'}, ...state.companyList]?.reduce((res, item) => {
      if (codeArr.includes(item.id)) res.push({id: item.id, name: item.name});
      return res
    }, [])
  }

  const onFinish = () => {
    onFormRef()?.validateFields().then(values => {
      props.setLoading?.(true)
      const orgIdList = values.shops ? getShopArr(values.shops) : []
      const values_ = {
        ...values,
        standardJson: {orgIdList, budgetRule: ruleModal.value},
        genItemId: props.genItemId
      }
      const apiUrl = values.id ? generalStandardUpdateById : generalStandardSave
      if(props.genItemId){
        apiUrl(values_).then(res => {
          message.success(res.message || '操作成功');
          props.fn?.(res);
        }).finally(() => props.setLoading?.(false))
      } else {
        props.fn?.({...values_, key: values.key || +new Date});
        props.setLoading?.(false)
      }

    })
  }

  useImperativeHandle(ref, () => {
    return {
      onFinish,
    }
  }, [onFinish])

  return <>
    <SGeneralForms
        ref={formRef}
        isHideGobackBtn={true}
        isDetail={props.readOnly}
        initialValues={props.record.id ? {} : {requiredFlag: true, editFlag: true}}
        layout={'vertical'}
        titleMode={props.readOnly ? '' : 'edit'}
        configs={[
          {
            title: '基础信息',
            formItem: [
              {name: 'id', hidden: true},
              {name: 'key', hidden: true},
              {name: 'genItemId', hidden: true},
              {
                name: 'requiredFlag',
                label: '是否必选',
                spanCol: 24,
                type: 'switch',
                comConfig: {
                  checkedChildren: '是',
                  unCheckedChildren: '否',
                }
              },
              {
                name: 'productId',
                label: '选择产品',
                required: !props.readOnly,
                rules: [{required: true, message: '产品不能为空'}],
                spanCol: 24,
                children: <DebounceSearchSelect readOnly={props.readOnly} fetchOptions={getProductManagementPage} label={'产品'}
                                                fieldNames={{label: 'prodName'}} query={{id: state.initValues.productId}}
                                                onChange={(v, o) => o && onFormRef()?.setFieldValue('showName', o.label)}
                />,
              },
              {
                name: 'showName',
                label: '显示名称',
                required: !props.readOnly,
                rules: [{required: true, message: '显示名称不能为空'}],
                spanCol: 24,
                comConfig: {
                  showCount: true, maxLength: 15,
                  onChange: (e) => onFormRef()?.setFieldValue('showName', trimStr(e.target.value))
                }
              },
            ],
          },
          {
            title: '预算量规则',
            titleMode: 'drawer',
            formItem: [
              {
                label: '预算量规则',
                spanCol: 24,
                [props.readOnly ? 'name' : '']: 'budgetRule',
                [props.readOnly ? '' : 'children']: <div>
                  <Button onClick={() => setRuleModal({open: true})}>编辑规则</Button>
                  {ruleModal?.value && <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} disabled style={{marginTop: 6}} value={replaceRule(ruleModal.value)}/>}
                </div>
              },
              {
                name: 'editFlag',
                label: '是否可编辑量',
                spanCol: 24,
                type: 'switch',
                comConfig: {
                  checkedChildren: '是',
                  unCheckedChildren: '否',
                }
              },
            ],
          },
          {
            title: '适用渠道',
            titleMode: 'drawer',
            formItem: [
              {
                name: 'shops',
                label: '适用门店',
                spanCol: 24,
                children: props.readOnly ? <div>{state.initValues.shops?.map(item => item.name)?.join(', ')}</div>
                    : <SLocalTreeSelected treeCheckable treeDefaultExpandAll
                                          showCheckedStrategy={TreeSelect.SHOW_PARENT}
                                          fieldNames={{label: 'name', value: 'id'}}
                                          treeData={[{name: '通用', id: '0'}, ...state.companyList]}/>,
              },
            ],
          },
        ]}
    />
    <RulesModal modalProps={{...ruleModal}}
                onCancel={() => setRuleModal({open: false})}
                type={'generalBudgetItem'}
                onOk={(value: string) => setRuleModal({value})}
    />
  </>
})
